import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';
import { titleize } from 'underscore.string';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@cargomatic/ui';
import { Info } from 'lucide-react';
import CargomaticDatePicker from 'components/v2/CargomaticDatePicker';
import ToggleSwitch from 'components/v2/forms/ToggleSwitch';
import { CustomerSearchCombobox } from 'components/design-system/organisms/CustomerSearchCombobox';
import {
  assetTypes,
  equipmentTypes,
  equipmentSizes,
  equipmentTypesLocalizationSlugs,
} from 'constants/Equipment';

const EQUIPMENT_ID_LENGTH_RANGE = [10, 11];
const DISABLED_ORDER_STATUSES = ['created', 'en_route'];

const SectionHeader = ({ title, children }) => (
  <div className="mb-3 flex items-center gap-2 border-b border-[hsl(var(--border))] pb-2">
    <span className="text-sm font-semibold text-[hsl(var(--muted-foreground))]">
      {title}
    </span>
    {children}
  </div>
);

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.any,
};

const InfoTooltip = ({ label }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        aria-label={label}
        className="text-[hsl(var(--muted-foreground))]"
      >
        <Info className="h-4 w-4" />
      </button>
    </TooltipTrigger>
    <TooltipContent side="top">{label}</TooltipContent>
  </Tooltip>
);

InfoTooltip.propTypes = {
  label: PropTypes.string.isRequired,
};

class EquipmentForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: {},
    };
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (!_.isEqual(this.props.error, props.error)) {
      this.setState((currentState) => {
        const { error: currentError } = currentState;
        return {
          error: _.merge(currentError, props.error),
        };
      });
    }

    if (this.props.success !== props.success) {
      this.setState({ success: props.success });
    }
  }

  hasError(field) {
    return !!this.state.error[field];
  }

  getSaveButtonLabel() {
    const { isNew } = this.props;
    if (isNew) {
      return 'ADD_NEW_EQUIPMENT';
    }
    return 'SAVE_EQUIPMENT';
  }

  hasEquipmentOrder() {
    const {
      equipment: { order },
    } = this.props;

    return !!order;
  }

  importDatesDisabled() {
    if (!this.hasEquipmentOrder()) {
      return true;
    }

    const {
      equipment: { order },
    } = this.props;
    return order.order_type !== 'import';
  }

  exportDatesDisabled() {
    if (!this.hasEquipmentOrder()) {
      return true;
    }

    const {
      equipment: { order },
    } = this.props;
    return order.order_type !== 'export';
  }

  handleChange = (field) => (e) =>
    this.props.onFieldChange(field, e.target.value);

  handleSelectChange = (field) => (value) =>
    this.props.onFieldChange(field, value);

  handleToggleChange = (field) => (value) => {
    this.props.onFieldChange(field, value);
  };

  handleDateFieldChange = (field) => (date) => {
    let mapped = null;
    if (date) {
      mapped = date.toISOString();
    }
    this.props.onFieldChange(field, mapped);
  };

  handleSave = () => {
    const error = this.validate();
    if (Object.keys(error).length === 0) {
      this.props.onSave();
    } else {
      this.setState({
        error,
      });
    }
  };
  handleCancel = () => {
    this.props.onCancel();
  };

  validate = () => {
    const error = {};

    const {
      equipment: {
        equipment_id,
        _equipmentType,
        _equipmentSize,
        _type,
        status,
        chassis_owner_type,
        is_private,
        chassis_owner,
      },
      t,
    } = this.props;

    if (!equipment_id) {
      error.equipment_id = t('ENTER_EQUIPMENT_NUMBER');
    } else if (
      _equipmentType !== 'trailer' &&
      !EQUIPMENT_ID_LENGTH_RANGE.includes(equipment_id.length)
    ) {
      error.equipment_id = t('EQUIPMENT_LENGTH_ERROR', {
        startRange: EQUIPMENT_ID_LENGTH_RANGE[0],
        endRange: EQUIPMENT_ID_LENGTH_RANGE[1],
      });
    }

    if (!_equipmentType) {
      error._equipmentType = t('PLEASE_SELECT_EQUIPMENT_TYPE');
    } else if (_equipmentType === 'container') {
      if (!_type) {
        error._type = t('PLEASE_SELECT_ASSET_TYPE');
      }
      if (!_equipmentSize) {
        error._equipmentSize = t('PLEASE_SELECT_EQUIPMENT_SIZE');
      }
    }

    if (!status) {
      error.status = t('PLEASE_SELECT_EQUIPMENT_STATUS');
    }

    const isChassisOwnerTypeRequired =
      _equipmentType === 'chassis' && is_private && !chassis_owner_type;
    if (isChassisOwnerTypeRequired) {
      error.chassis_owner_type = t('PLEASE_SELECT_CHASSIS_OWNER');
    }

    const isCustomerRequired =
      _equipmentType === 'chassis' &&
      is_private &&
      chassis_owner_type &&
      chassis_owner_type === 'customer' &&
      !chassis_owner;
    if (isCustomerRequired) {
      error.chassis_owner = t('PLEASE_SELECT_CUSTOMER');
    }

    return error;
  };

  disableStatusDropdown = () => {
    const { order, _equipmentType } = this.props.equipment;

    if (_equipmentType && _equipmentType === 'container') {
      if (order) {
        const { order_type, status } = order;

        if (
          order_type === 'import' &&
          DISABLED_ORDER_STATUSES.includes(status)
        ) {
          return true;
        }
      }
    }
    return false;
  };

  disableOwnershipDropdown = () => {
    const { is_private } = this.props.equipment;
    return !!is_private;
  };

  renderSuccess() {
    const { success } = this.state;
    const { t } = this.props;

    if (!success) {
      return null;
    }
    return (
      <Alert>
        <AlertTitle>{t(success)}</AlertTitle>
      </Alert>
    );
  }

  renderStatusDropdown() {
    const { equipment, t } = this.props;
    const disabled = this.disableStatusDropdown();

    const statusSelect = (
      <Select
        value={equipment.status || ''}
        onValueChange={this.handleSelectChange('status')}
        disabled={disabled}
      >
        <SelectTrigger
          className="w-full"
          aria-invalid={this.hasError('status')}
        >
          <SelectValue placeholder={t('SELECT_EQUIPMENT_STATUS')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="unavailable">
            {titleize(t('UNAVAILABLE', { count: 1 }))}
          </SelectItem>
          <SelectItem value="available">
            {titleize(t('AVAILABLE', { count: 1 }))}
          </SelectItem>
          <SelectItem value="closed_area">{t('CLOSED_AREA')}</SelectItem>
          <SelectItem
            className="equipment-equipmentForm-toggleOnHold"
            value="on_hold"
          >
            {t('ON_HOLD')}
          </SelectItem>
        </SelectContent>
      </Select>
    );

    if (disabled) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="block">{statusSelect}</span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {t('EQUIPMENT_STATUS_CAN_NOT_UPDATE_NOTE')}
          </TooltipContent>
        </Tooltip>
      );
    }
    return statusSelect;
  }

  renderImportDateFields() {
    const disabled = this.importDatesDisabled();
    const {
      equipment: { last_free_date },
      t,
    } = this.props;

    return (
      <div>
        <SectionHeader title={t('IMPORT_DATES')}>
          <InfoTooltip label={t('ATTACHED_IMPORT_ORDER_TO_SET_DATE_FIELDS')} />
        </SectionHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DateField
            label={t('LAST_FREE_DATE')}
            disabled={disabled}
            selected={last_free_date}
            onChange={this.handleDateFieldChange('last_free_date')}
          />
        </div>
      </div>
    );
  }

  renderExportDateFields() {
    const disabled = this.exportDatesDisabled();
    const {
      equipment: { earliest_return_date, last_cut_date, earliest_empty_date },
      t,
    } = this.props;

    return (
      <div>
        <SectionHeader title={t('EXPORT_DATES')}>
          <InfoTooltip label={t('ATTACHED_EXPORT_ORDER_TO_SET_DATE_FIELDS')} />
        </SectionHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DateField
            label={t('LAST_CUT_DATE')}
            disabled={disabled}
            selected={last_cut_date}
            onChange={this.handleDateFieldChange('last_cut_date')}
          />
          <DateField
            label={t('EARLIEST_DATE_TO_GET_EMPTY')}
            disabled={disabled}
            selected={earliest_empty_date}
            onChange={this.handleDateFieldChange('earliest_empty_date')}
          />
          <DateField
            label={t('EARLIEST_RETURN_DATE')}
            disabled={disabled}
            selected={earliest_return_date}
            onChange={this.handleDateFieldChange('earliest_return_date')}
          />
        </div>
      </div>
    );
  }

  renderErrors() {
    const { error } = this.state;
    const errorCount = Object.keys(error).length;
    if (errorCount === 0) {
      return null;
    }
    const { t } = this.props;
    return (
      <Alert variant="destructive">
        <AlertTitle>{t('ERRORS')}</AlertTitle>
        <AlertDescription>
          <ul>
            {Object.keys(error).map((key) => (
              <li key={key}>{error[key]}</li>
            ))}
          </ul>
        </AlertDescription>
      </Alert>
    );
  }

  renderCharacterCount() {
    const {
      equipment: { equipment_id },
      t,
    } = this.props;

    const length = (equipment_id || '').length;
    const unit = t('CHARACTER', { count: length });

    return (
      <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
        (<strong>{length}</strong> {unit})
      </p>
    );
  }

  render() {
    const { t } = this.props;
    if (!this.props.equipment) {
      return null;
    }

    let equipmentIdPlaceholder;

    const { equipment, isAdmin } = this.props;

    if (this.props.equipment._equipmentType !== 'trailer') {
      equipmentIdPlaceholder = t('DEFAULT_EQUIPMENT_PLACEHOLDER', {
        startRange: EQUIPMENT_ID_LENGTH_RANGE[0],
        endRange: EQUIPMENT_ID_LENGTH_RANGE[1],
      });
    } else {
      equipmentIdPlaceholder = t('EQUIPMENT_NUMBER');
    }
    return (
      <TooltipProvider>
        <div id="add-equipment-form" className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
            {this.renderErrors()}
            <fieldset className="m-0 space-y-4 border-0 p-0">
              <div>
                <Label className="mb-1 block">{t('EQUIPMENT_NUMBER')}</Label>
                <Input
                  type="text"
                  className="w-full"
                  placeholder={equipmentIdPlaceholder}
                  value={this.props.equipment.equipment_id}
                  onChange={this.handleChange('equipment_id')}
                  aria-invalid={this.hasError('equipment_id')}
                />
                {this.renderCharacterCount()}
              </div>
              {this.props.isAdmin && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="mb-1 block">{t('ASSET_TYPE')}</Label>
                    <Select
                      value={this.props.equipment._equipmentType || ''}
                      onValueChange={this.handleSelectChange('_equipmentType')}
                    >
                      <SelectTrigger
                        className="w-full"
                        aria-invalid={this.hasError('_equipmentType')}
                      >
                        <SelectValue placeholder={t('SELECT_ASSET_TYPE')} />
                      </SelectTrigger>
                      <SelectContent>
                        {assetTypes.map((assetType) => (
                          <SelectItem key={assetType} value={assetType}>
                            {titleize(t(assetType.toUpperCase()))}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1 block">
                      {t('EQUIPMENT_STATUS')}
                    </Label>
                    {this.renderStatusDropdown()}
                  </div>
                </div>
              )}
              {isAdmin && equipment?._equipmentType === 'chassis' && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="mb-1 block">{t('PRIVATE')}</Label>
                    <ToggleSwitch
                      data-testid="equipment-is-private-switch"
                      enabled={!!equipment?.is_private}
                      onChange={this.handleToggleChange('is_private')}
                    />
                  </div>
                  {this.disableOwnershipDropdown() && (
                    <div>
                      <Label className="mb-1 block">
                        {t('CHASSIS_OWNERSHIP')}
                      </Label>
                      <Select
                        value={equipment?.chassis_owner_type || ''}
                        onValueChange={this.handleSelectChange(
                          'chassis_owner_type',
                        )}
                      >
                        <SelectTrigger
                          className="w-full"
                          data-testid="equipment-owner-type-dropdown"
                          aria-invalid={this.hasError('chassis_owner_type')}
                        >
                          <SelectValue
                            placeholder={t(
                              'SELECT_EQUIPMENT_CHASSIS_OWNERSHIP',
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cargomatic">
                            {titleize(t('CARGOMATIC', { count: 1 }))}
                          </SelectItem>
                          <SelectItem value="customer">
                            {titleize(t('CUSTOMER', { count: 1 }))}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {equipment?.chassis_owner_type === 'customer' && (
                    <div>
                      <Label className="mb-1 block">{t('CUSTOMER')}</Label>
                      <CustomerSearchCombobox
                        id="equipment-chassis-owner-search"
                        value={equipment?.chassis_owner || null}
                        onValueChange={(item) =>
                          this.props.onFieldChange('chassis_owner', item)
                        }
                        placeholder={t('SEARCH_FOR_CUSTOMER')}
                      />
                    </div>
                  )}
                </div>
              )}
              {this.props.isAdmin && this.hasEquipmentOrder() && (
                <div className="space-y-4">
                  {this.renderImportDateFields()}
                  {this.renderExportDateFields()}
                </div>
              )}
              {this.props.equipment._equipmentType === 'container' && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="mb-1 block">{t('EQUIPMENT_TYPE')}</Label>
                    <Select
                      value={this.props.equipment._type || ''}
                      onValueChange={this.handleSelectChange('_type')}
                    >
                      <SelectTrigger
                        className="w-full"
                        aria-invalid={this.hasError('_type')}
                      >
                        <SelectValue placeholder={t('SELECT_EQUIPMENT_TYPE')} />
                      </SelectTrigger>
                      <SelectContent>
                        {_.map(equipmentTypes, (label, key) => (
                          <SelectItem key={key} value={key}>
                            {t(equipmentTypesLocalizationSlugs[key])}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1 block">{t('EQUIPMENT_SIZE')}</Label>
                    <Select
                      value={this.props.equipment._equipmentSize || ''}
                      onValueChange={this.handleSelectChange('_equipmentSize')}
                    >
                      <SelectTrigger
                        className="w-full"
                        aria-invalid={this.hasError('_equipmentSize')}
                      >
                        <SelectValue placeholder={t('SELECT_EQUIPMENT_SIZE')} />
                      </SelectTrigger>
                      <SelectContent>
                        {equipmentSizes.map((size) => (
                          <SelectItem key={size} value={size}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </fieldset>
            {this.renderSuccess()}
          </div>
          <div className="flex shrink-0 justify-end gap-2 border-t border-[hsl(var(--border))] px-6 py-4">
            <Button variant="outline" onClick={this.handleCancel}>
              {t('CANCEL')}
            </Button>
            <Button onClick={this.handleSave}>
              {t(this.getSaveButtonLabel())}
            </Button>
          </div>
        </div>
      </TooltipProvider>
    );
  }
}

EquipmentForm.propTypes = {
  isNew: PropTypes.bool,
  isAdmin: PropTypes.bool,
  equipment: PropTypes.object,
  error: PropTypes.object,
  success: PropTypes.string,
  onFieldChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

EquipmentForm.defaultProps = {
  isNew: false,
  isAdmin: false,
};

function DateField({ disabled, label, selected, onChange }) {
  return (
    <div>
      {label && <Label className="mb-1 block">{label}</Label>}
      <CargomaticDatePicker
        className="w-full"
        selected={selected}
        onChange={onChange}
        disabled={disabled}
        isClearable
      />
    </div>
  );
}

DateField.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
  selected: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

DateField.defaultProps = {
  disabled: false,
  label: '',
};

export default withTranslation()(EquipmentForm);
